import React, { useState, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Alert,
  Animated,
  Pressable,
  ScrollView,
  Image,
  Modal,
} from "react-native";
import { Ionicons, AntDesign } from "@expo/vector-icons";
import MembershipCard from "../MembershipCard";
import PerksComponent from "./PerksComponent";
import ProgressBarComponent from "./ProgressBarComponent";
import GiftFlowComponent from "./GiftFlowComponent";
import GiftPassWorkflow from "../GiftPassWorkflow";
import PassesComponent from "../PassesComponent";
import { ImageBackground } from 'react-native';
import { StyleSheet, Dimensions } from 'react-native';
import { useLoggedInUserPointsForRestaurant } from '../../hooks/useLoggedInUserPointsForRestaurant';
import { useUserTierAndPoints } from '../../hooks/useUserTierAndPoints';
import { getCurrentTierIndex, MEMBERSHIP_TIERS } from '../../utils/tierChecker';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import RestaurantTabsComponent from './RestaurantTabsComponent';
import { OpeningHoursModalButton } from './OpeningHoursCollapsible';
import CopyButton from './HousePick/CopyButton';
import Constants from 'expo-constants';
import * as Linking from 'expo-linking';
import { colors } from '../../constants/theme';
import SaveToListModal from '../SaveToListModal';
import { useGooglePlaceDetails } from '../../hooks/useGooglePlaceDetails';
import PlacePhotoCarousel from '../InternalTool/PlacePhotoCarousel';

function resolveGooglePlaceId(restaurant) {
  if (!restaurant) return null;
  if (restaurant.googlePlaceId && typeof restaurant.googlePlaceId === "string") {
    return restaurant.googlePlaceId;
  }
  if (restaurant.isGooglePlace && restaurant.id && typeof restaurant.id === "string") {
    return restaurant.id;
  }
  return null;
}

/** Google Place photos shown in place of the membership card. Hidden when no Place ID. */
function RestaurantPlacePhotos({ photoUrls, isLoading, hasGooglePlaceId }) {
  if (!hasGooglePlaceId) return null;
  if (isLoading && (!photoUrls || photoUrls.length === 0)) {
    return (
      <View
        style={{
          height: 200,
          borderRadius: 12,
          backgroundColor: '#F0EAE6',
          marginTop: 8,
        }}
      />
    );
  }
  if (!photoUrls || photoUrls.length === 0) return null;
  return (
    <View style={{ marginTop: 8 }}>
      <PlacePhotoCarousel photoUrls={photoUrls} height={200} />
    </View>
  );
}

// Wrapper component for Specials and Passes sections with expand functionality
const SpecialsAndPassesWrapper = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <View style={styles.specialsWrapper}>
        {/* Expand Button - Placed at the top above all content */}
        <View style={styles.specialsHeader}>
          <Text style={styles.specialsHeaderText}>Daily Specials & Passes</Text>
          <TouchableOpacity
            onPress={() => setIsExpanded(true)}
            style={styles.expandButton}
          >
            <Ionicons name="expand" size={24} color="#6B4C3B" />
          </TouchableOpacity>
        </View>
        {/* Content (PerksComponent and PassesComponent) */}
        <View style={styles.specialsContent}>
          {children}
        </View>
      </View>

      {/* Expanded Modal View */}
      {isExpanded && (
        <Modal
          visible={isExpanded}
          animationType="slide"
          transparent={true}
          onRequestClose={() => setIsExpanded(false)}
        >
          <Pressable
            style={styles.expandedModalOverlay}
            onPress={() => setIsExpanded(false)}
          >
            <Pressable
              style={styles.expandedModalContent}
              onPress={(e) => e.stopPropagation()}
            >
              <View style={styles.expandedModalHeader}>
                <Text style={styles.expandedModalTitle}>Daily Specials & Passes</Text>
                <TouchableOpacity
                  onPress={() => setIsExpanded(false)}
                  style={styles.expandedModalCloseButton}
                >
                  <Ionicons name="close" size={28} color="#6B4C3B" />
                </TouchableOpacity>
              </View>
              <ScrollView
                style={styles.expandedModalScrollView}
                contentContainerStyle={styles.expandedModalScrollContent}
              >
                {children}
              </ScrollView>
            </Pressable>
          </Pressable>
        </Modal>
      )}
    </>
  );
};

// User Points Display Component
function UserPointsDisplay({ selectedRestaurant }) {
  const { userPoints } = useUserTierAndPoints(selectedRestaurant);

  return (
    <View style={styles.userPointsContainer}>
      <Text style={styles.userPointsText}>
        Your Points: {userPoints || 0}
      </Text>
    </View>
  );
}

function FullTierProgressBar({ selectedRestaurant }) {
  const { userPoints, currentTierIndex } = useUserTierAndPoints(selectedRestaurant);

  const animatedValue = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    Animated.timing(animatedValue, {
      toValue: Math.max(0, currentTierIndex + 1),
      duration: 500,
      useNativeDriver: false,
    }).start();
  }, [currentTierIndex]);

  // Get tier requirements from restaurant data
  const getTierRequirement = (tierName) => {
    const tier = selectedRestaurant?.membershipTiers?.find(t => t.tier === tierName);
    return tier ? `${tier.checkinRequirement}+ pts` : '0+ pts';
  };

  return (
    <View style={styles.fullTierProgressContainer}>
      <View style={styles.fullTierProgressCard}>
        <View style={styles.fullTierProgressInner}>
          {/* Circles and connecting lines row */}
          <View style={styles.fullTierProgressRow}>
            {MEMBERSHIP_TIERS.map((tier, index) => (
              <React.Fragment key={tier}>
                <View style={styles.fullTierCircleContainer}>
                  {/* Map pin icon for current tier */}
                  {index === currentTierIndex && (
                    <View style={styles.fullTierPinIcon}>
                      <Ionicons name="location" size={14} color="#6B4C3B" />
                    </View>
                  )}
                  
                  {/* Tier circle */}
                  <View
                    style={[
                      styles.fullTierCircle,
                      index <= currentTierIndex
                        ? styles.fullTierCircleActive
                        : styles.fullTierCircleInactive
                    ]}
                  />
                </View>
                
                {/* Connecting line */}
                {index < MEMBERSHIP_TIERS.length - 1 && (
                  <View
                    style={[
                      styles.fullTierConnectingLine,
                      index < currentTierIndex ? styles.fullTierConnectingLineActive : styles.fullTierConnectingLineInactive
                    ]}
                  />
                )}
              </React.Fragment>
            ))}
          </View>
          
          {/* Text labels row */}
          <View style={styles.fullTierTextRow}>
            {MEMBERSHIP_TIERS.map((tier, index) => (
              <View key={`${tier}-text`} style={styles.fullTierTextContainer}>
                {/* Tier name */}
                <Text style={styles.fullTierName} numberOfLines={1} adjustsFontSizeToFit>
                  {tier}
                </Text>
                
                {/* Point requirement */}
                <Text style={styles.fullTierRequirement}>
                  {getTierRequirement(tier)}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    </View>
  );
}

// Add the non-partner card image import
const nonPartnerCardImage = require('../../assets/nonDisgoPartnerMembershipCard.jpg');

// View 1: User has membership & restaurant is a Disgo partner
const PartnerWithMembershipView = ({
  selectedRestaurant,
  membership,
  handleGiftPerk,
  handleDirections,
  getNextMembershipTier,
  getUserMembership,
  handleBenefitPress,
  getCurrentMembershipTier,
  handleMissingPerk,
  doesUserHaveMembership,
  handleCheckIn,
  userDoc,
  onUsePass, // New prop for handling pass use
  onCloseUsePassModal, // New prop for handling modal close
  authenticated, // New prop for authentication status
  login // New prop for login function
}) => (
  <>
    <View style={styles.progressBarSection}>
      <ProgressBarComponent
        selectedRestaurant={selectedRestaurant}
        getNextMembershipTier={getNextMembershipTier}
        getUserMembership={getUserMembership}
      />
    </View>
    <View style={styles.checkInButtonRow}>
      <TouchableOpacity
        onPress={() => {
          if (authenticated) {
            handleCheckIn(selectedRestaurant._id);
          } else {
            login();
          }
        }}
        style={styles.checkInButton}
      >
        <Text style={styles.checkInButtonText}>
          {authenticated ? "Share Bite" : "Login to share bites"}
        </Text>
      </TouchableOpacity>
    </View>
    <SpecialsAndPassesWrapper>
      <PerksComponent
        selectedRestaurant={selectedRestaurant}
        doesUserHaveMembership={doesUserHaveMembership}
        getUserMembership={getUserMembership}
        handleBenefitPress={handleBenefitPress}
        getNextMembershipTier={getNextMembershipTier}
        getCurrentMembershipTier={getCurrentMembershipTier}
        handleMissingPerk={handleMissingPerk}
        userDoc={userDoc}
      />
      
      <PassesComponent
        restaurantId={selectedRestaurant._id}
        userDoc={userDoc}
        getUserMembership={getUserMembership}
        selectedRestaurant={selectedRestaurant}
        onUsePass={onUsePass}
        onCloseUsePassModal={onCloseUsePassModal}
      />
    </SpecialsAndPassesWrapper>
    
    {/* Membership Card moved below tabs */}
    <View style={styles.membershipCardContainer}>
      <MembershipCard
        restaurantName={selectedRestaurant.name}
        mintedDate={membership?.mintedDate}
        ownerName={membership?.ownerName}
        tier={membership?.tier}
        membershipId={membership?.id || "0"}
        referralCount={membership?.referralCount || "0"}
        checkInCount={membership?.checkinCount || "0"}
        imgUrl={selectedRestaurant.imgUrl}
      />
    </View>
    
    <View style={styles.userPointsSection}>
      {/* User Points Display */}
      <UserPointsDisplay selectedRestaurant={selectedRestaurant} />
      <FullTierProgressBar selectedRestaurant={selectedRestaurant} />
    </View>
  </>
);

// View 2: User does not have membership & restaurant is a Disgo partner
const PartnerNoMembershipView = ({
  selectedRestaurant,
  handleBenefitPress,
  getCurrentMembershipTier,
  getNextMembershipTier,
  getUserMembership,
  doesUserHaveMembership,
  handleMissingPerk,
  handleCheckIn,
  userDoc,
  onUsePass, // New prop for handling pass use
  onCloseUsePassModal, // New prop for handling modal close
  authenticated, // New prop for authentication status
  login // New prop for login function
}) => (
  <>
    <View style={styles.progressBarSection}>
      <ProgressBarComponent
        selectedRestaurant={selectedRestaurant}
        getNextMembershipTier={getNextMembershipTier}
        getUserMembership={getUserMembership}
      />
    </View>
    <View style={styles.checkInButtonRow}>
      <TouchableOpacity
        onPress={() => {
          if (authenticated) {
            handleCheckIn(selectedRestaurant._id);
          } else {
            login();
          }
        }}
        style={styles.checkInButton}
      >
        <Text style={styles.checkInButtonText}>
          {authenticated ? "Share Bite" : "Login to share bites"}
        </Text>
      </TouchableOpacity>
    </View>
    <SpecialsAndPassesWrapper>
      <PerksComponent
        selectedRestaurant={selectedRestaurant}
        doesUserHaveMembership={doesUserHaveMembership}
        getUserMembership={getUserMembership}
        handleBenefitPress={handleBenefitPress}
        getNextMembershipTier={getNextMembershipTier}
        getCurrentMembershipTier={getCurrentMembershipTier}
        handleMissingPerk={handleMissingPerk}
        userDoc={userDoc}
      />
      
      <PassesComponent
        restaurantId={selectedRestaurant._id}
        userDoc={userDoc}
        getUserMembership={getUserMembership}
        selectedRestaurant={selectedRestaurant}
        onUsePass={onUsePass}
        onCloseUsePassModal={onCloseUsePassModal}
      />
    </SpecialsAndPassesWrapper>
    
    {/* Membership Card moved below tabs */}
    <View style={styles.membershipCardContainer}>
      <MembershipCard
        restaurantName={selectedRestaurant.name}
        mintedDate={null}
        ownerName={null}
        tier={null}
        membershipId="0"
        referralCount="0"
        checkInCount="0"
        imgUrl={selectedRestaurant.imgUrl}
      />
    </View>
    
    <View style={styles.userPointsSection}>
      {/* User Points Display */}
      <UserPointsDisplay selectedRestaurant={selectedRestaurant} />
      <FullTierProgressBar selectedRestaurant={selectedRestaurant} />
    </View>
  </>
);

// Consolidated Non-Partner View (combines membership + no-membership)
const NonPartnerView = ({
  selectedRestaurant,
  membership,
  handleDirections,
  handleCheckIn,
  userDoc,
  doesUserHaveMembership,
  photoUrls,
  isGooglePlaceDetailsLoading,
}) => (
  <>
    {/* COMMENTED OUT: Specials, Shared Bites, and Passes sections for non-partnered restaurants */}
    {/*
    <View style={styles.perksSection}>
      <PerksComponent
        selectedRestaurant={selectedRestaurant}
        doesUserHaveMembership={doesUserHaveMembership}
        getUserMembership={() => membership}
        userDoc={userDoc}
      />
    </View>
    */}
    
    {/* COMMENTED OUT: Membership Card for non-partnered restaurants */}
    {/*
    <View style={styles.membershipCardContainerBottom}>
      <MembershipCard
        restaurantName={selectedRestaurant?.name}
        mintedDate={membership?.mintedDate || null}
        ownerName={membership?.ownerName || null}
        tier={membership?.tier || null}
        membershipId={membership?.id || "0"}
        referralCount={membership?.referralCount || "0"}
        checkInCount={membership?.checkinCount || "0"}
        imgUrl={selectedRestaurant?.imgUrl}
        isPartner={false}
        />
    </View>
    */}
    <RestaurantPlacePhotos
      photoUrls={photoUrls}
      isLoading={isGooglePlaceDetailsLoading}
      hasGooglePlaceId={!!resolveGooglePlaceId(selectedRestaurant)}
    />
  </>
);

function BottomSheetContent({
  selectedRestaurant,
  navigation,
  handleDirections,
  handleCenterMap,
  doesUserHaveMembership,
  getUserMembership,
  getCurrentMembershipTier,
  getNextMembershipTier,
  handleCheckIn,
  handleClaimMembership,
  handleBenefitPress,
  userDoc,
  onUsePass, // New prop for handling pass use
  onCloseUsePassModal, // New prop for handling modal close
  onClose, // New prop for closing the bottom sheet
  userCheckinHistory, // New prop for check-in history
  authenticated, // New prop for authentication status
  login, // New prop for login function
  hideCloseButton = false // New prop to hide close button (for desktop side sheet)
}) {
  const [isGiftFlowVisible, setIsGiftFlowVisible] = useState(false);
  const [availableBenefits, setAvailableBenefits] = useState([]);
  const [isGiftPassWorkflowVisible, setIsGiftPassWorkflowVisible] = useState(false);
  const [selectedPassForGift, setSelectedPassForGift] = useState(null);
  const [showSaveListModal, setShowSaveListModal] = useState(false);

  const googlePlaceId = resolveGooglePlaceId(selectedRestaurant);
  const {
    address: googleAddress,
    openingHours: googleOpeningHours,
    photoUrls: googlePhotoUrls,
    phoneNumber: googlePhoneNumber,
    websiteUri: googleWebsiteUri,
    openNow: googleOpenNow,
    price: googlePrice,
    editorialSummary: googleEditorialSummary,
    isLoading: isGooglePlaceDetailsLoading,
  } = useGooglePlaceDetails(googlePlaceId);
  const displayAddress =
    (selectedRestaurant?.isGooglePlace &&
      typeof selectedRestaurant.address === "string" &&
      selectedRestaurant.address.trim()) ||
    googleAddress ||
    null;
  const displayOpeningHours = googleOpeningHours;
  const displayPrice =
    (typeof googlePrice === "string" && googlePrice.trim()) ||
    (typeof selectedRestaurant?.price === "string" && selectedRestaurant.price.trim()) ||
    null;
  const disgoVenueDescription =
    typeof selectedRestaurant?.venueDescription === "string"
      ? selectedRestaurant.venueDescription.trim()
      : "";
  const displayVenueDescription =
    disgoVenueDescription ||
    (typeof googleEditorialSummary === "string" && googleEditorialSummary.trim()) ||
    null;

  // Query for friends average rating for this restaurant
  const friendsRatingData = useQuery(
    userDoc?._id && selectedRestaurant?._id
      ? api.friends.getFriendsAverageRating
      : "skip",
    userDoc?._id && selectedRestaurant?._id
      ? { userId: userDoc._id, restaurantId: selectedRestaurant._id }
      : "skip"
  ) || { averageRating: null, ratingCount: 0 };

  const listsContainingRestaurant = useQuery(
    userDoc?.privyDid && selectedRestaurant?._id && !selectedRestaurant?.isGooglePlace
      ? api.savedLists.listsContainingRestaurant
      : "skip",
    userDoc?.privyDid && selectedRestaurant?._id && !selectedRestaurant?.isGooglePlace
      ? { privyDid: userDoc.privyDid, restaurantId: selectedRestaurant._id }
      : "skip"
  );
  const isSavedToAnyList =
    Array.isArray(listsContainingRestaurant) && listsContainingRestaurant.length > 0;

  // Helper function to convert average rating to crave/solid/toss system
  const getFriendsRatingBadge = (averageRating) => {
    if (!averageRating) return null;
    
    if (averageRating >= 4.1) {
      return { emoji: '🤤', label: 'Crave' };
    } else if (averageRating >= 3.0) {
      return { emoji: '😌', label: 'Solid' };
    } else {
      return { emoji: '🫢', label: 'Toss' };
    }
  };

  const handleGiftPerk = async () => {
    try {
      const membership = getUserMembership(selectedRestaurant.name);
      if (!membership) {
        Alert.alert("Error", "Membership not found.");
        return;
      }
      setAvailableBenefits(
        membership.benefits.filter(
          (b) =>
            b.original_user === null ||
            b.original_user === undefined ||
            b.original_user === b.user
        )
      );
      setIsGiftFlowVisible(true);
    } catch (error) {
      console.error("Error fetching benefits:", error);
      Alert.alert("Error", "Unable to fetch benefits.");
    }
  };

  const handleCloseGiftFlow = () => {
    setIsGiftFlowVisible(false);
  };

  const handleGiftPassWorkflow = async (pass) => {
    setSelectedPassForGift(pass);
    setIsGiftPassWorkflowVisible(true);
  };

  const handleCloseGiftPassWorkflow = () => {
    setIsGiftPassWorkflowVisible(false);
    setSelectedPassForGift(null);
  };

  // Wrapper function to pass to view components
  const handleGiftPassWrapper = (pass) => {
    handleGiftPassWorkflow(pass);
  };

  const handleMissingPerk = () => {
    if (navigation) {
      navigation.navigate("Missing");
    }
  };

  const userHasMembership = doesUserHaveMembership(selectedRestaurant.name);
  const membership = userHasMembership
    ? getUserMembership(selectedRestaurant.name)
    : null;

  const scrollViewRef = useRef(null);

  return (
      <>
      <SaveToListModal
        visible={showSaveListModal}
        onClose={() => setShowSaveListModal(false)}
        restaurantId={selectedRestaurant?._id}
        privyDid={userDoc?.privyDid}
      />
      <ScrollView 
        ref={scrollViewRef}
        contentContainerStyle={{ 
          paddingBottom: 100,
          paddingHorizontal: 16,
          paddingTop: 24,
        }} 
        showsVerticalScrollIndicator={true}
        scrollEventThrottle={16}
      >
        {/* Header: title + actions on one line */}
        <View>
            <View style={styles.headerTitleAndActionsRow}>
              <View style={styles.headerTitleBlock}>
                <Text style={styles.restaurantName} numberOfLines={3}>
                  {selectedRestaurant.name}
                </Text>
              </View>
              <View style={styles.headerTrailingActions}>
                <Pressable onPress={handleDirections} style={styles.iconButton}>
                  <Ionicons name="navigate" size={24} color="#4e2916" />
                </Pressable>
                <Pressable onPress={handleCenterMap} style={styles.iconButton}>
                  <Ionicons name="locate" size={24} color="#4e2916" />
                </Pressable>
                {!hideCloseButton && (
                  <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                    <Ionicons name="close" size={20} color="#4e2916" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
            {displayVenueDescription ? (
              <Text style={styles.descriptionText}>{displayVenueDescription}</Text>
            ) : null}
            {displayAddress ? (
              <View style={styles.addressRowWithCopy}>
                <Text
                  style={[styles.addressTextBelowTitle, styles.addressTextFlex]}
                  numberOfLines={2}
                >
                  {displayAddress}
                </Text>
                <CopyButton textToCopy={displayAddress} />
              </View>
            ) : null}
            <Text style={styles.cuisineText}>
              {selectedRestaurant.cuisine}{displayPrice ? ` • ${displayPrice}` : ''}
              {friendsRatingData.averageRating ? (() => {
                const ratingBadge = getFriendsRatingBadge(friendsRatingData.averageRating);
                if (!ratingBadge) return null;
                return (
                  <>
                    {' • '}
                    <Text style={styles.cuisineRatingEmoji}>{ratingBadge.emoji}</Text>
                    {` ${ratingBadge.label} (${friendsRatingData.averageRating.toFixed(1)})`}
                  </>
                );
              })() : ` • No friends rated`}
            </Text>
            {userCheckinHistory?.includes(selectedRestaurant._id) && (
              <View style={styles.visitedPillRow}>
                <View style={styles.visitedPill} accessibilityLabel="You have visited this place" accessibilityRole="text">
                  <Text style={styles.visitedPillText}>Visited</Text>
                </View>
              </View>
            )}
            {(userDoc?.privyDid && selectedRestaurant?._id && !selectedRestaurant?.isGooglePlace) ||
            googlePhoneNumber ||
            googleWebsiteUri ||
            (Array.isArray(displayOpeningHours) && displayOpeningHours.length > 0) ? (
              <View style={styles.placeActionsRow}>
                {Array.isArray(displayOpeningHours) && displayOpeningHours.length > 0 ? (
                  <OpeningHoursModalButton
                    hours={displayOpeningHours}
                    openNow={googleOpenNow}
                    variant="chip"
                  />
                ) : null}
                {googlePhoneNumber ? (
                  <View style={styles.placeActionChipWrap}>
                    <Pressable
                      onPress={() => Linking.openURL(`tel:${googlePhoneNumber.replace(/[^\d+]/g, '')}`)}
                      style={styles.placeActionChip}
                      accessibilityLabel={`Call ${googlePhoneNumber}`}
                    >
                      <Ionicons name="call" size={18} color="#4e2916" />
                      <Text style={styles.placeActionChipText} numberOfLines={1}>Call</Text>
                    </Pressable>
                  </View>
                ) : null}
                {googleWebsiteUri ? (
                  <View style={styles.placeActionChipWrap}>
                    <Pressable
                      onPress={() => Linking.openURL(googleWebsiteUri)}
                      style={styles.placeActionChip}
                      accessibilityLabel="Open website"
                    >
                      <Ionicons name="globe-outline" size={18} color="#4e2916" />
                      <Text style={styles.placeActionChipText} numberOfLines={1}>Website</Text>
                    </Pressable>
                  </View>
                ) : null}
                {userDoc?.privyDid && selectedRestaurant?._id && !selectedRestaurant?.isGooglePlace ? (
                  <View style={styles.placeActionChipWrap}>
                    <Pressable
                      onPress={() => setShowSaveListModal(true)}
                      style={styles.placeActionChip}
                      accessibilityLabel={isSavedToAnyList ? 'Saved to a list' : 'Save to list'}
                    >
                      <Ionicons
                        name={isSavedToAnyList ? 'bookmark' : 'bookmark-outline'}
                        size={18}
                        color="#4e2916"
                      />
                      <Text style={styles.placeActionChipText} numberOfLines={1}>
                        {isSavedToAnyList ? 'Saved' : 'Save'}
                      </Text>
                    </Pressable>
                  </View>
                ) : null}
              </View>
            ) : null}
            <View style={styles.specialsTabsSection}>
              <RestaurantTabsComponent
                selectedRestaurant={selectedRestaurant}
              />
            </View>
        </View>

        {/* Get the Disgo App Promotional Card for Non-Partnered Restaurants */}
        {!selectedRestaurant?.isPartner && (
          <>
            <View style={styles.appPromoCard}>
              <View style={styles.appPromoHeader}>
                <View style={styles.appPromoIcon}>
                  <View style={styles.appIconInner}>
                    <Ionicons name="phone-portrait" size={24} color="#6B4C3B" />
                  </View>
                </View>
                <View style={styles.appPromoTitleContainer}>
                  <Text style={styles.appPromoTitle}>Get the Disgo App</Text>
                  <Text style={styles.appPromoSubtitle}>Unlock the full experience at {selectedRestaurant.name}</Text>
                </View>
              </View>
              <View style={styles.appPromoFeatures}>
                <View style={styles.appPromoFeature}>
                  <Ionicons name="checkmark-circle" size={16} color="#6B4C3B" />
                  <Text style={styles.appPromoFeatureText}>Check in and share your visit</Text>
                </View>
                <View style={styles.appPromoFeature}>
                  <Ionicons name="checkmark-circle" size={16} color="#6B4C3B" />
                  <Text style={styles.appPromoFeatureText}>View friends' ratings and reviews</Text>
                </View>
                <View style={styles.appPromoFeature}>
                  <Ionicons name="checkmark-circle" size={16} color="#6B4C3B" />
                  <Text style={styles.appPromoFeatureText}>Create and share moments</Text>
                </View>
                <View style={styles.appPromoFeature}>
                  <Ionicons name="checkmark-circle" size={16} color="#6B4C3B" />
                  <Text style={styles.appPromoFeatureText}>Use passes</Text>
                </View>
              </View>
              <View style={styles.appPromoButtons}>
                <TouchableOpacity 
                  onPress={() => Linking.openURL('https://apps.apple.com/us/app/disgoapp/id6480529083')}
                  style={styles.appStoreButtonWrapper}
                >
                  <Image 
                    source={require('../../assets/store designs/apple.webp')}
                    style={styles.appStoreButtonImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
                <TouchableOpacity 
                  onPress={() => Linking.openURL('https://play.google.com/store/apps/details?id=com.disgoapp.disgo')}
                  style={styles.googlePlayButtonWrapper}
                >
                  <Image 
                    source={require('../../assets/store designs/android.webp')}
                    style={styles.googlePlayButtonImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              </View>
            </View>
            
            {/* Check-in Button */}
            <View style={styles.checkInButtonContainer}>
              <TouchableOpacity
                onPress={() => {
                  if (authenticated) {
                    handleCheckIn(selectedRestaurant._id);
                  } else {
                    login();
                  }
                }}
                style={styles.checkInButton}
              >
                <Text style={styles.checkInButtonText}>
                  {authenticated ? "Share Bite" : "Login to share bites"}
                </Text>
              </TouchableOpacity>
            </View>
          </>
        )}

        {/* Progress Bar and Check-in Button for Partnered Views */}
        {selectedRestaurant?.isPartner && (
          <>
            <View style={[styles.progressBarSection, styles.progressBarSectionPadded]}>
              <ProgressBarComponent
                selectedRestaurant={selectedRestaurant}
                getNextMembershipTier={getNextMembershipTier}
                getUserMembership={getUserMembership}
              />
            </View>
            
            {/* Get the Disgo App Promotional Card */}
            <View style={styles.appPromoCard}>
              <View style={styles.appPromoHeader}>
                <View style={styles.appPromoIcon}>
                  <View style={styles.appIconInner}>
                    <Ionicons name="phone-portrait" size={24} color="#6B4C3B" />
                  </View>
                </View>
                <View style={styles.appPromoTitleContainer}>
                  <Text style={styles.appPromoTitle}>Get the Disgo App</Text>
                  <Text style={styles.appPromoSubtitle}>Unlock the full experience at {selectedRestaurant.name}</Text>
                </View>
              </View>
              <View style={styles.appPromoFeatures}>
                <View style={styles.appPromoFeature}>
                  <Ionicons name="checkmark-circle" size={16} color="#6B4C3B" />
                  <Text style={styles.appPromoFeatureText}>Check in and share your visit</Text>
                </View>
                <View style={styles.appPromoFeature}>
                  <Ionicons name="checkmark-circle" size={16} color="#6B4C3B" />
                  <Text style={styles.appPromoFeatureText}>View friends' ratings and reviews</Text>
                </View>
                <View style={styles.appPromoFeature}>
                  <Ionicons name="checkmark-circle" size={16} color="#6B4C3B" />
                  <Text style={styles.appPromoFeatureText}>Create and share moments</Text>
                </View>
                <View style={styles.appPromoFeature}>
                  <Ionicons name="checkmark-circle" size={16} color="#6B4C3B" />
                  <Text style={styles.appPromoFeatureText}>Use passes</Text>
                </View>
              </View>
              <View style={styles.appPromoButtons}>
                <TouchableOpacity 
                  onPress={() => Linking.openURL('https://apps.apple.com/us/app/disgoapp/id6480529083')}
                  style={styles.appStoreButtonWrapper}
                >
                  <Image 
                    source={require('../../assets/store designs/apple.webp')}
                    style={styles.appStoreButtonImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
                <TouchableOpacity 
                  onPress={() => Linking.openURL('https://play.google.com/store/apps/details?id=com.disgoapp.disgo')}
                  style={styles.googlePlayButtonWrapper}
                >
                  <Image 
                    source={require('../../assets/store designs/android.webp')}
                    style={styles.googlePlayButtonImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              </View>
            </View>
            
            {/* Check-in Button for Partnered Views */}
            <View style={[styles.checkInButtonContainer, styles.checkInButtonContainerPadded]}>
              <TouchableOpacity
                onPress={() => {
                  if (authenticated) {
                    handleCheckIn(selectedRestaurant._id);
                  } else {
                    login();
                  }
                }}
                style={styles.checkInButton}
              >
                <Text style={styles.checkInButtonText}>
                  {authenticated ? "Share Bite" : "Login to share bites"}
                </Text>
              </TouchableOpacity>
            </View>
          </>
        )}

        {/* Content */}
        <View style={styles.contentContainer}>
          {selectedRestaurant?.isPartner ? (
            // Partnered restaurants - only tabs and membership card
            <>
              {/* COMMENTED OUT: Specials, Shared Bites, and Passes sections */}
              {/*
              <SpecialsAndPassesWrapper>
                <PerksComponent
                  selectedRestaurant={selectedRestaurant}
                  doesUserHaveMembership={doesUserHaveMembership}
                  getUserMembership={getUserMembership}
                  handleBenefitPress={handleBenefitPress}
                  getNextMembershipTier={getNextMembershipTier}
                  getCurrentMembershipTier={getCurrentMembershipTier}
                  handleMissingPerk={handleMissingPerk}
                  userDoc={userDoc}
                />
                <PassesComponent
                  getUserMembership={getUserMembership}
                  selectedRestaurant={selectedRestaurant}
                  onUsePass={onUsePass}
                  onCloseUsePassModal={onCloseUsePassModal}
                />
              </SpecialsAndPassesWrapper>
              */}
              
              {/* COMMENTED OUT: Membership Card (Tier Card) */}
              {/*
              <View style={styles.membershipCardContainer}>
                <MembershipCard
                  restaurantName={selectedRestaurant.name}
                  mintedDate={membership?.mintedDate}
                  ownerName={membership?.ownerName}
                  tier={membership?.tier}
                  membershipId={membership?.id || "0"}
                  referralCount={membership?.referralCount || "0"}
                  checkInCount={membership?.checkinCount || "0"}
                  imgUrl={selectedRestaurant.imgUrl}
                  isPartner={true}
                  selectedRestaurant={selectedRestaurant}
                />
              </View>
              */}
              <RestaurantPlacePhotos
                photoUrls={googlePhotoUrls}
                isLoading={isGooglePlaceDetailsLoading}
                hasGooglePlaceId={!!resolveGooglePlaceId(selectedRestaurant)}
              />
            </>
                    ) : (
                      // Non-partnered restaurants - consolidated view
                      <NonPartnerView
                        selectedRestaurant={selectedRestaurant}
                        membership={membership}
                        handleDirections={handleDirections}
                        handleCheckIn={handleCheckIn}
                        userDoc={userDoc}
                        doesUserHaveMembership={userHasMembership}
                        photoUrls={googlePhotoUrls}
                        isGooglePlaceDetailsLoading={isGooglePlaceDetailsLoading}
                      />
                     )}
        </View>
      </ScrollView>
      
      <GiftFlowComponent
        visible={isGiftFlowVisible}
        onClose={handleCloseGiftFlow}
        benefits={availableBenefits}
        membershipId={membership?._id}
        selectedRestaurant={selectedRestaurant}
      />
      </>
  );
}

const styles = StyleSheet.create({
  specialsWrapper: {
    backgroundColor: '#F8F6F3',
    marginTop: 8,
    marginHorizontal: -24,
  },
  specialsContent: {
    paddingTop: 8,
  },
  userPointsContainer: {
    marginBottom: 16,
  },
  userPointsText: {
    fontSize: 18,
    fontFamily: 'AbhayaLibre_700Bold, serif',
    textAlign: 'center',
    color: '#4d2c1d',
  },
  headerTitleAndActionsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    width: '100%',
    gap: 8,
    marginBottom: 6,
  },
  headerTitleBlock: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: 0,
  },
  addressRowWithCopy: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginTop: 0,
    paddingHorizontal: 0,
    gap: 6,
  },
  addressTextBelowTitle: {
    marginTop: 4,
    paddingHorizontal: 0,
    fontFamily: 'AbhayaLibre_400Regular, serif',
    color: '#9C8A80',
    fontSize: 14,
    lineHeight: 20,
  },
  addressTextFlex: {
    flexGrow: 0,
    flexShrink: 1,
    maxWidth: 'calc(100% - 38px)',
    marginTop: 0,
    paddingHorizontal: 0,
  },
  headerTrailingActions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    gap: 4,
    paddingTop: 2,
  },
  restaurantName: {
    flex: 1,
    fontSize: 28,
    lineHeight: 32,
    fontFamily: 'AbhayaLibre_700Bold, serif',
    color: '#000000',
  },
  visitedPillRow: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  visitedPill: {
    backgroundColor: colors.brownMuted,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  visitedPillText: {
    fontSize: 12,
    fontFamily: 'AbhayaLibre_700Bold, serif',
    color: '#FFFFFF',
  },
  iconButton: {
    borderRadius: 8,
    padding: 4,
  },
  iconButtonMargin: {
    marginLeft: 12,
  },
  closeButton: {
    width: 32,
    height: 32,
    backgroundColor: '#e5e7eb',
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cuisineText: {
    fontFamily: 'AbhayaLibre_700Bold, serif',
    paddingHorizontal: 0,
    color: '#000000',
    fontSize: 16,
    lineHeight: 20,
    marginBottom: 0,
    paddingVertical: 0,
  },
  cuisineRatingEmoji: {
    fontSize: 13,
    lineHeight: 20,
  },
  specialsTabsSection: {
    marginTop: 12,
    width: '100%',
  },
  placeActionsRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    width: '100%',
    gap: 8,
    marginTop: 12,
  },
  placeActionChipWrap: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  placeActionChip: {
    width: '100%',
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    backgroundColor: '#F3E8E2',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#E5D9D2',
  },
  placeActionChipText: {
    fontFamily: 'AbhayaLibre_700Bold, serif',
    fontSize: 14,
    color: '#4e2916',
  },
  descriptionText: {
    marginTop: 4,
    marginBottom: 0,
    fontFamily: 'AbhayaLibre_400Regular, serif',
    paddingHorizontal: 0,
    color: '#4d2c1d',
    fontSize: 14,
    lineHeight: 20,
  },
  checkInButtonContainer: {
    flexDirection: 'row',
    marginTop: 8,
    gap: 16,
  },
  checkInButtonContainerPadded: {
    paddingHorizontal: 12,
  },
  checkInButton: {
    backgroundColor: '#5E3D2D',
    borderRadius: 8,
    paddingVertical: 9,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
    paddingRight: 16,
  },
  checkInButtonText: {
    color: '#FFFFFF',
    fontSize: 20,
    fontFamily: 'AbhayaLibre_700Bold, serif',
  },
  checkInButtonRow: {
    flexDirection: 'row',
    marginTop: 8,
    gap: 16,
  },
  progressBarSection: {
    marginTop: 24,
  },
  progressBarSectionPadded: {
    paddingHorizontal: 12,
  },
  progressBarContainer: {
    paddingHorizontal: 12,
    paddingBottom: 12,
    marginTop: 8,
  },
  progressBarInner: {
    flexDirection: 'column',
  },
  progressBarWrapper: {
    height: 28,
    borderRadius: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    backgroundColor: '#e5e7eb',
  },
  progressBarFill: {
    backgroundColor: '#6B4C3B',
    height: '100%',
    borderTopRightRadius: 16,
    borderBottomRightRadius: 16,
  },
  progressBarText: {
    fontSize: 16,
    fontFamily: 'AbhayaLibre_400Regular, serif',
    marginTop: 8,
    textAlign: 'right',
    color: '#000000',
  },
  membershipCardContainer: {
    marginTop: 4,
  },
  membershipCardContainerBottom: {
    marginTop: 8,
  },
  userPointsSection: {
    marginTop: 8,
  },
  contentContainer: {
    width: '100%',
    marginTop: 0,
    paddingHorizontal: 8,
  },
  perksSection: {
    marginTop: 8,
  },
  speedRailzButtonContainer: {
    marginTop: 8,
    marginBottom: 8,
  },
  speedRailzButton: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
    padding: 8,
    backgroundColor: '#FEF3C7', // yellow-300 equivalent
    borderRadius: 12,
  },
  speedRailzButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  speedRailzButtonTextContainer: {
    marginLeft: 8,
    flex: 1,
  },
  speedRailzButtonTitle: {
    fontSize: 20,
    fontFamily: 'AbhayaLibre_700Bold',
    color: '#000000',
  },
  speedRailzButtonSubtitle: {
    fontSize: 14,
    fontFamily: 'AbhayaLibre_400Regular',
    color: '#374151',
    marginTop: 2,
  },
  appPromoCard: {
    backgroundColor: '#F9F5F3',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#6B4C3B',
    padding: 16,
    marginTop: 16,
    marginBottom: 16,
  },
  appPromoHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  appPromoIcon: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#6B4C3B',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  appIconInner: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#8B6F47',
    justifyContent: 'center',
    alignItems: 'center',
  },
  appPromoTitleContainer: {
    flex: 1,
  },
  appPromoTitle: {
    fontSize: 20,
    fontFamily: 'AbhayaLibre_700Bold',
    color: '#6B4C3B',
    marginBottom: 4,
  },
  appPromoSubtitle: {
    fontSize: 14,
    fontFamily: 'AbhayaLibre_400Regular',
    color: '#8B6F47',
  },
  appPromoFeatures: {
    marginBottom: 16,
  },
  appPromoFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  appPromoFeatureText: {
    fontSize: 14,
    fontFamily: 'AbhayaLibre_400Regular',
    color: '#6B4C3B',
    marginLeft: 8,
  },
  appPromoButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  appStoreButtonWrapper: {
    flex: 1,
  },
  appStoreButtonImage: {
    width: '100%',
    height: 50,
  },
  googlePlayButtonWrapper: {
    flex: 1,
  },
  googlePlayButtonImage: {
    width: '100%',
    height: 50,
  },
  fullTierProgressContainer: {
    paddingBottom: 80,
  },
  fullTierProgressCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
  },
  fullTierProgressInner: {
    paddingHorizontal: 8,
  },
  fullTierProgressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fullTierCircleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  fullTierPinIcon: {
    position: 'absolute',
    top: -24,
    zIndex: 20,
  },
  fullTierCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
  },
  fullTierCircleActive: {
    backgroundColor: '#6B4C3B',
    borderColor: '#6B4C3B',
  },
  fullTierCircleInactive: {
    backgroundColor: '#FFFFFF',
    borderColor: '#d1d5db',
  },
  fullTierConnectingLine: {
    width: 32,
    height: 2,
  },
  fullTierConnectingLineActive: {
    backgroundColor: '#6B4C3B',
  },
  fullTierConnectingLineInactive: {
    backgroundColor: '#d1d5db',
  },
  fullTierTextRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  fullTierTextContainer: {
    flex: 1,
    alignItems: 'center',
  },
  fullTierName: {
    fontSize: 12,
    fontFamily: 'AbhayaLibre_700Bold, serif',
    color: '#6B4C3B',
    textAlign: 'center',
  },
  fullTierRequirement: {
    fontSize: 12,
    fontFamily: 'AbhayaLibre_400Regular, serif',
    color: '#6B4C3B',
    textAlign: 'center',
  },
  specialsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingHorizontal: 4,
  },
  specialsHeaderText: {
    fontSize: 20,
    fontFamily: 'AbhayaLibre_700Bold, serif',
    color: '#6B4C3B',
  },
  expandButton: {
    padding: 4,
  },
  expandedModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  expandedModalContent: {
    backgroundColor: '#F9F5F3',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: '92%',
    maxHeight: '98%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  expandedModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  expandedModalTitle: {
    fontSize: 24,
    fontFamily: 'AbhayaLibre_700Bold, serif',
    color: '#6B4C3B',
  },
  expandedModalCloseButton: {
    padding: 4,
  },
  expandedModalScrollView: {
    flex: 1,
  },
  expandedModalScrollContent: {
    padding: 16,
    paddingBottom: 100,
  },
});

export default BottomSheetContent;

